'use client'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Department, WorkerListItem } from '@garage/shared'
import {
  Button,
  IconButton,
  ConfirmDialog,
  EmptyState,
  Skeleton,
  useSnackbar,
  AddIcon,
  EditIcon,
  DeleteIcon,
  PeopleIcon,
} from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { DepartmentFormDialog } from './DepartmentFormDialog.js'

/**
 * Every worker grouped by department, so "who's in Bodywork" is a glance
 * rather than a scan down the flat Workers table. Departments themselves are
 * managed here (add/rename/remove); a worker's department is set on the
 * worker (WorkerFormDialog) — this view is a picture of that assignment,
 * not a second place to edit it.
 */
export function DepartmentsView() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const { data: departmentsData, isLoading: departmentsLoading } = useQuery({
    queryKey: qk.departments,
    queryFn: () => api.departments.list(),
  })
  const { data: workersData, isLoading: workersLoading } = useQuery({
    queryKey: qk.workers(),
    queryFn: () => api.workers.list(),
  })

  const [formDialogOpen, setFormDialogOpen] = useState(false)
  const [editingDepartment, setEditingDepartment] = useState<Department | undefined>()
  const [deleteTarget, setDeleteTarget] = useState<Department | null>(null)

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.departments.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.departments })
      queryClient.invalidateQueries({ queryKey: qk.workers() })
      snackbar.success(t('departmentForm.departmentRemoved'))
      setDeleteTarget(null)
    },
    onError: (error) => {
      snackbar.error(error instanceof ApiError ? error.message : t('departmentForm.failedToRemove'))
    },
  })

  const handleAddNew = () => {
    setEditingDepartment(undefined)
    setFormDialogOpen(true)
  }

  const handleEdit = (department: Department) => {
    setEditingDepartment(department)
    setFormDialogOpen(true)
  }

  const handleFormClose = () => {
    setFormDialogOpen(false)
    setEditingDepartment(undefined)
  }

  if (departmentsLoading || workersLoading) {
    return <Skeleton lines={4} />
  }

  const departments = departmentsData?.items ?? []
  const workers = workersData?.items ?? []

  const workersByDepartment = new Map<string, WorkerListItem[]>()
  const unassigned: WorkerListItem[] = []
  for (const worker of workers) {
    if (worker.departmentId) {
      const list = workersByDepartment.get(worker.departmentId) ?? []
      list.push(worker)
      workersByDepartment.set(worker.departmentId, list)
    } else {
      unassigned.push(worker)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outlined" size="sm" icon={<AddIcon className="w-4 h-4" />} onClick={handleAddNew}>
          {t('departmentForm.addDepartment')}
        </Button>
      </div>

      {departments.length === 0 ? (
        <EmptyState
          icon={<PeopleIcon className="w-12 h-12 text-on-surface-variant" />}
          title={t('departmentForm.noDepartmentsYet')}
          description={t('departmentForm.addDepartmentDescription')}
          action={
            <Button variant="tonal" onClick={handleAddNew}>
              {t('departmentForm.addADepartment')}
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {departments.map((department) => {
            const members = workersByDepartment.get(department.id) ?? []
            return (
              <div
                key={department.id}
                className="bg-surface-container border border-outline-variant rounded-lg p-4 flex flex-col gap-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-title-md text-on-surface truncate">{department.name}</div>
                    <div className="text-body-sm text-on-surface-variant">
                      {t('departmentForm.memberCount', { count: department.workerCount })}
                    </div>
                  </div>
                  <div className="flex gap-0.5 flex-shrink-0">
                    <IconButton
                      icon={<EditIcon className="w-4 h-4" />}
                      label={t('departmentForm.editDepartmentLabel', { department: department.name })}
                      variant="standard"
                      size="sm"
                      onClick={() => handleEdit(department)}
                    />
                    <IconButton
                      icon={<DeleteIcon className="w-4 h-4" />}
                      label={t('departmentForm.removeDepartmentLabel', { department: department.name })}
                      variant="danger"
                      size="sm"
                      onClick={() => setDeleteTarget(department)}
                    />
                  </div>
                </div>

                {members.length === 0 ? (
                  <p className="text-body-sm text-on-surface-variant">{t('departmentForm.noMembersYet')}</p>
                ) : (
                  <div className="space-y-1">
                    {members.map((worker) => (
                      <button
                        key={worker.id}
                        type="button"
                        onClick={() => navigate(`/staff/${worker.id}`)}
                        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-start interactive hover:bg-surface-high"
                      >
                        <span className="text-body-sm text-on-surface truncate">{worker.name}</span>
                        {worker.phone && (
                          <span className="ref text-label-sm text-on-surface-variant flex-shrink-0">{worker.phone}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {unassigned.length > 0 && (
        <div className="bg-surface-container border border-outline-variant rounded-lg p-4">
          <div className="text-title-md text-on-surface mb-1">{t('departmentForm.unassigned')}</div>
          <p className="text-body-sm text-on-surface-variant mb-3">{t('departmentForm.unassignedDescription')}</p>
          <div className="space-y-1">
            {unassigned.map((worker) => (
              <button
                key={worker.id}
                type="button"
                onClick={() => navigate(`/staff/${worker.id}`)}
                className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-start interactive hover:bg-surface-high"
              >
                <span className="text-body-sm text-on-surface truncate">{worker.name}</span>
                {worker.phone && (
                  <span className="ref text-label-sm text-on-surface-variant flex-shrink-0">{worker.phone}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <DepartmentFormDialog open={formDialogOpen} department={editingDepartment} onClose={handleFormClose} />

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('departmentForm.removeDepartmentTitle')}
        message={t('departmentForm.removeDepartmentMessage', { department: deleteTarget?.name })}
        confirmLabel={t('common.remove')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id)
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
